import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import axios from "../../../my-axios";
import UserContext from "../../../contexts/UserContext";
import ContentContext from "../../../contexts/ContentContext";
import DataTable from "../../controls/DataTable";
import DataTablePagination from "../../controls/DataTablePagination";
import Spinner from "../../controls/Spinner";
import MsgBox from "../../controls/MsgBox";
import { formatDateTime, getErrorDesc } from "../../../utils/common";

/*
This page allows ADMIN users to manage API keys.
ADMIN can:
1. create new API keys
  1.1 if successful, show the generated token in a modal for copying
  1.2 if 500 failed, show error message
  1.3 if 401 unauthorized, redirect to signout
2. view existing API keys and their details
  2.1 use existing DataTable and Pagination components to show the list
  2.2 if 500 failed, show error message
*/
const LIST_ENDPOINT = "/api-keys";

const TABLE_COLUMNS = [
  "prefix",
  "status",
  "created_at",
  "last_used_at",
  "last_ip",
  "usage_count",
];

const TABLE_LABELS = [
  "PREFIX",
  "STATUS",
  "CREATED AT",
  "LAST USED AT",
  "LAST IP",
  "USAGE COUNT",
];

const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
};

export default function ApiKeys() {
  const navigate = useNavigate();
  const userCtx = useContext(UserContext);
  const contentCtx = useContext(ContentContext);

  const [tableState, setTableState] = useState(INITIAL_STATE);
  const [currentPage, setCurrentPage] = useState({
    page: 1,
    size: 10,
    total: 0,
  });
  const hasInitialisedRef = useRef(false);
  const ensuredUserRef = useRef(false);
  const redirectedRef = useRef(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const { state, getToken, getUser } = userCtx;

  const { updatePath } = contentCtx;

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    zIndex: 1050,
  };

  const modalCardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    maxWidth: "520px",
    width: "100%",
    padding: "2rem",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
    position: "relative",
    textAlign: "center",
  };

  const closeButtonStyle = {
    position: "absolute",
    top: "0.75rem",
    right: "0.75rem",
    border: "none",
    background: "transparent",
    fontSize: "1.25rem",
    color: "#6c757d",
    cursor: "pointer",
  };

  const modalKeyStyle = {
    fontFamily: "monospace",
    backgroundColor: "#f1f3f5",
    borderRadius: "6px",
    padding: "0.75rem 1rem",
    margin: "1rem 0",
    wordBreak: "break-all",
    color: "#212529",
  };

  const loadKeys = useCallback(async () => {
    setTableState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const token = getToken && getToken();
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const response = await axios.get(LIST_ENDPOINT);
      const items = Array.isArray(response.data.data) ? response.data.data : [];

      const formatted = items.map((item) => ({
        ...item,
        created_at: item.created_at ? formatDateTime(item.created_at) : "",
        last_used_at: item.last_used_at
          ? formatDateTime(item.last_used_at)
          : "—",
        last_ip: item.last_ip || "—",
        usage_count:
          typeof item.usage_count === "number" ? item.usage_count : "—",
      }));

      setTableState({ data: formatted, loading: false, error: null });
      setCurrentPage((prev) => ({ ...prev, total: formatted.length }));
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate("/signout");
        return;
      }
      setTableState({ data: [], loading: false, error: getErrorDesc(error) });
    }
  }, [getToken, navigate]);

  const createApiKey = async () => {
    setTableState((prev) => ({ ...prev, loading: true }));
    setGeneratedToken(null);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    setCopied(false);

    try {
      const token = getToken && getToken();
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const response = await axios.post(LIST_ENDPOINT);
      const { data } = response.data || {};

      if (data?.token) {
        setGeneratedToken(data.token);
        setShowModal(true);
      }

      await loadKeys();
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate("/signout");
        return;
      }
      setTableState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorDesc(error),
      }));
    }
  };

  const handleCopyToken = useCallback(async (token) => {
    if (!token || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy API key", err);
    }
  }, []);

  const closeModal = useCallback(() => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    setCopied(false);
    setGeneratedToken(null);
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (!ensuredUserRef.current && typeof getUser === "function") {
      getUser();
      ensuredUserRef.current = true;
    }
  }, [getUser]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const role = state?.role;
    if (!role) {
      return;
    }

    if (role !== "ADMIN") {
      if (!redirectedRef.current) {
        navigate("/");
        redirectedRef.current = true;
      }
      return;
    }

    if (!hasInitialisedRef.current) {
      updatePath([{ title: "Manage API Keys", link: "/manage-api-keys" }]);
      loadKeys();
      hasInitialisedRef.current = true;
    }
  }, [state?.role, navigate, updatePath, loadKeys]);

  const changePage = (page) => {
    setCurrentPage((prev) => ({ ...prev, page }));
  };

  const changeSize = (size) => {
    setCurrentPage((prev) => ({ ...prev, size }));
  };

  const paginatedData = tableState.data.slice(
    (currentPage.page - 1) * currentPage.size,
    currentPage.page * currentPage.size
  );

  return (
    <>
      <div className="row">
        <div className="col-12 mb-4 d-flex justify-content-between align-items-center">
          <h5 className="m-0">API Keys</h5>
          <Button
            variant="success"
            onClick={createApiKey}
          disabled={tableState.loading}
        >
          New
        </Button>
      </div>

      <div className="col-12">
        <div style={{ position: "relative", width: "100%", overflowX: "auto" }}>
          <DataTable
            module="api-keys"
            id="prefix"
            list={{
              data: paginatedData,
              columns: TABLE_COLUMNS,
              labels: TABLE_LABELS,
            }}
          />
        </div>
      </div>

      <div
        className="col-12"
        style={{
          paddingTop: "10px",
          textAlign: "center",
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        <DataTablePagination
          page={currentPage.page}
          size={currentPage.size}
          total={currentPage.total}
          setPage={changePage}
          setSize={changeSize}
        />
      </div>

      <div className="col-12">
        <Spinner value={tableState.loading} />
        {tableState.error && (
          <MsgBox
            value={{ type: "error", title: "Error", msg: tableState.error }}
          />
        )}
      </div>
      </div>

      {showModal && generatedToken && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true">
          <div style={modalCardStyle}>
            <button
              type="button"
              style={closeButtonStyle}
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </button>
            <h4 className="mb-3 font-weight-bold text-success">API Key Created!</h4>
            <p className="mb-2">
              A new API key has been generated successfully. For your security, this is the only time the key will be displayed.
            </p>
            <p className="mb-3">
              Please copy and store it in a secure location before closing this window.
            </p>
            <div style={modalKeyStyle}>{generatedToken}</div>
            <button
              type="button"
              className="btn btn-success btn-block"
              onClick={() => handleCopyToken(generatedToken)}
            >
              {copied ? (
                <>
                  <i className="fas fa-check mr-2" aria-hidden="true" />
                  COPIED!
                </>
              ) : (
                <>
                  <i className="far fa-copy mr-2" aria-hidden="true" />
                  Copy API Key
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
